import {Record, List} from 'immutable';
import moment from 'moment-timezone';
import {type Moment} from 'moment-timezone';

import {
    type PlanState as GqlPlanState,
    type PlanPartState as GqlPlanPartState,
    type PlanStateAppliedFunding,
    type PlanStateAppliedSessionFunding,
} from 'web-app/util/graphql/api-types';

interface IPlanPartState {
    readonly monthlyEstimate: number;
    readonly weeklyTotal: number;
    readonly planPartId: string;
    readonly pricingGroupId: string;
}

export class PlanPartState extends Record<IPlanPartState>({
    monthlyEstimate: 0,
    weeklyTotal: 0,
    planPartId: '',
    pricingGroupId: '',
}) {}

interface IFundedBooking {
    readonly bookingId: string;
    readonly fundedMinutes: number | null;
}

class FundedBooking extends Record<IFundedBooking>({
    bookingId: '',
    fundedMinutes: null,
}) {}

interface IPublicFunding {
    readonly amount: number;
    readonly claimable: number;
    readonly applied: number;
    readonly hours: number;
    readonly minutes: number | null;
    readonly period: string;
    readonly fundedBookings: List<FundedBooking>;
}

export class PublicFundingState extends Record<IPublicFunding>({
    amount: 0,
    claimable: 0,
    applied: 0,
    hours: 0,
    minutes: null,
    period: '',
    fundedBookings: List(),
}) {}

interface IPlanState {
    readonly from?: Moment;
    readonly to?: Moment;
    readonly ageGroupId: string;
    readonly pricingGroupId: string;
    readonly monthlyEstimate: number;
    readonly planPartStates: List<PlanPartState>;
    readonly publicFunding: PublicFundingState;
}

export class PlanState extends Record<IPlanState>({
    from: undefined,
    to: undefined,
    ageGroupId: '',
    pricingGroupId: '',
    monthlyEstimate: 0,
    planPartStates: List(),
    publicFunding: new PublicFundingState(),
}) {}

const fundedBookingFromApi = (booking: any) => new FundedBooking(booking);

const fundedBookingFromGraphQL = (booking: PlanStateAppliedSessionFunding) =>
    new FundedBooking({
        bookingId: booking.sessionBookingId,
        fundedMinutes: booking.minutes,
    });

const publicFundingFromApi = (publicFunding: any) =>
    new PublicFundingState({
        ...publicFunding,
        fundedBookings: List(publicFunding.fundedBookings).map(fundedBookingFromApi),
    });

const publicFundingFromGraphQL = (appliedFunding: PlanStateAppliedFunding) =>
    new PublicFundingState({
        amount: Number.parseFloat(appliedFunding.amount),
        hours: appliedFunding.time.__typename === 'PlanFundingTimeHours' ? appliedFunding.time.hours : 0,
        minutes: appliedFunding.time.__typename === 'PlanFundingTimeMinutes' ? appliedFunding.time.minutes : null,
        period: appliedFunding.period,
        fundedBookings: List(appliedFunding.fundedBookings).map(fundedBookingFromGraphQL),
    });

const planPartStateFromApi = (planPartState: any) => new PlanPartState(planPartState);

const planPartStateFromGraphQL = (planPartState: GqlPlanPartState) =>
    new PlanPartState({
        monthlyEstimate: Number.parseFloat(planPartState.monthlyEstimate),
        weeklyTotal: planPartState.weeklyTotal ? Number.parseFloat(planPartState.weeklyTotal) : undefined,
        planPartId: planPartState.planPartId,
        pricingGroupId: planPartState.pricingGroupId,
    });

const fromAPI = (planState: any) =>
    new PlanState({
        ...planState,
        to: planState.to ? moment(planState.to, 'YYYY-MM-DD') : undefined,
        from: planState.from ? moment(planState.from, 'YYYY-MM-DD') : undefined,
        planPartStates: List(planState.planPartStates).map(planPartStateFromApi),
        publicFunding: publicFundingFromApi(planState.publicFunding),
    });

const fromGraphQL = (planState: GqlPlanState) =>
    new PlanState({
        ageGroupId: planState.planPartState[0].pricingGroupId, // This field is deprecated and always equal to the pricingGroupId
        pricingGroupId: planState.planPartState[0].pricingGroupId,
        to: planState.range.to ? moment(planState.range.to, 'YYYY-MM-DD') : undefined,
        from: planState.range.from ? moment(planState.range.from, 'YYYY-MM-DD') : undefined,
        planPartStates: List(planState.planPartState).map(planPartStateFromGraphQL),
        publicFunding: planState.appliedFunding ? publicFundingFromGraphQL(planState.appliedFunding) : undefined,
    });

export default {
    record: PlanState,
    default: new PlanState(),
    fromAPI,
    fromGraphQL,
};
